import {
  base64ToArrayBuffer,
  derivePassphraseKey,
  unwrapDekWithPassphrase,
  unwrapDekWithWrappingKey,
  wrapDekWithPassphrase
} from '../utils/crypto'
import {
  cachePassphraseKey,
  loadPassphraseKey
} from '../utils/passphrase-cache'

/**
 * Account service for email + OTP authentication (ADR-0007, limited scope).
 *
 * The Data Encryption Key (DEK) is the same AES-GCM key used by the current
 * sync layer. Instead of being wrapped by a seed-derived key, it is wrapped
 * by a key derived from an app-generated six-word passphrase and stored in
 * `user_keys` as `salt.wrappedKey`.
 *
 * Framework-agnostic: the Supabase client is injected so the whole service
 * can be unit-tested against a fake.
 */

/**
 * `key_version` semantics: 1 = legacy seed-wrapped DEK, 2 = DGK-wrapped,
 * 3 = v6 passphrase-wrapped X25519 private key, 4 = passphrase-wrapped DEK.
 */
export const OTP_KEY_VERSION = 4
export const OTP_KEY_ALGORITHM = 'PBKDF2-AES-KW'

const SEED_EMAIL_SUFFIX = '@anon.minimind.es'

type AccountError = { message?: string } | null

export type AccountResult<T = unknown> = {
  data: T
  error: AccountError
}

export type AccountPostgrestBuilder = {
  upsert: (
    rows: unknown | unknown[],
    options?: { onConflict?: string }
  ) => AccountPostgrestBuilder
  select: (columns?: string) => AccountPostgrestBuilder
  maybeSingle: () => Promise<AccountResult>
  then: (
    resolve: (value: AccountResult) => void,
    reject?: (reason: unknown) => void
  ) => Promise<unknown>
}

export type AccountSupabaseClient = {
  auth: {
    signInWithOtp: (input: {
      email: string
      options?: { shouldCreateUser?: boolean }
    }) => Promise<AccountResult>
    verifyOtp: (input: {
      email: string
      token: string
      type: 'email'
    }) => Promise<AccountResult>
    updateUser: (
      input: { email: string },
      options?: { emailRedirectTo?: string }
    ) => Promise<AccountResult>
    getUser: () => Promise<{
      data: {
        user: { email?: string; email_confirmed_at?: string | null } | null
      } | null
    }>
    signOut: () => Promise<AccountResult>
  }
  from: (table: string) => AccountPostgrestBuilder
  rpc: (
    fn: string,
    args?: Record<string, unknown>
  ) => Promise<{ error: AccountError }>
}

export type UserKeyRow = {
  wrapped_key: string
  algorithm: string
  key_version: number
}

function errorMessage(error: AccountError): string | null {
  return error?.message ?? null
}

/**
 * Sends the email OTP for sign-in (`shouldCreateUser: false`) or sign-up
 * (`shouldCreateUser: true`, the default).
 */
export async function requestEmailOtp(
  client: AccountSupabaseClient,
  email: string,
  shouldCreateUser = true
): Promise<{ error: string | null }> {
  const { error } = await client.auth.signInWithOtp({
    email,
    options: { shouldCreateUser }
  })
  return { error: errorMessage(error) }
}

/**
 * Verifies the emailed OTP and creates the session.
 */
export async function verifyEmailOtp(
  client: AccountSupabaseClient,
  email: string,
  token: string
): Promise<{ error: string | null }> {
  const { error } = await client.auth.verifyOtp({
    email,
    token,
    type: 'email'
  })
  return { error: errorMessage(error) }
}

/**
 * Downloads the current user's wrapped key row, or null when absent.
 */
export async function fetchUserKey(
  client: AccountSupabaseClient
): Promise<UserKeyRow | null> {
  const { data, error } = await client
    .from('user_keys')
    .select('wrapped_key, algorithm, key_version')
    .maybeSingle()

  if (error) throw new Error(error.message)
  return (data as UserKeyRow | null) ?? null
}

/**
 * A seed account is identified by its derived anonymous email or by a
 * pre-OTP wrapped key. New OTP accounts (real email, no key row yet) are not.
 */
export async function isSeedAccount(
  client: AccountSupabaseClient
): Promise<boolean> {
  const { data } = await client.auth.getUser()
  const email = data?.user?.email ?? ''
  if (email.endsWith(SEED_EMAIL_SUFFIX)) return true

  try {
    const row = await fetchUserKey(client)
    return row !== null && row.key_version < OTP_KEY_VERSION
  } catch {
    // The key table may be unavailable during a partial rollout; not a seed
    // account is the safe default (the prompt simply does not open).
    return false
  }
}

/**
 * Creates the DEK for a new account and stores it passphrase-wrapped.
 */
export async function setupAccountKey(
  client: AccountSupabaseClient,
  userId: string,
  passphrase: string
): Promise<CryptoKey> {
  const dek = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  )
  await storePassphraseWrappedKey(client, userId, dek, passphrase)
  return dek
}

/**
 * Unwraps the account DEK using the supplied passphrase, or the cached
 * device key when no passphrase is given.
 */
export async function unlockAccount(
  client: AccountSupabaseClient,
  passphrase?: string
): Promise<CryptoKey> {
  const row = await fetchUserKey(client)
  if (!row) throw new Error('No wrapped key found for this account')
  if (row.key_version !== OTP_KEY_VERSION) {
    throw new Error('seed_account_requires_conversion')
  }

  if (passphrase) {
    const dek = await unwrapDekWithPassphrase(row.wrapped_key, passphrase)
    await cacheDerivedPassphraseKey(row.wrapped_key, passphrase)
    return dek
  }

  const cached = await loadPassphraseKey()
  if (!cached) throw new Error('passphrase_required')
  return unwrapDekWithWrappingKey(row.wrapped_key, cached)
}

/**
 * Re-wraps the existing DEK under the new passphrase during seed conversion.
 * The DEK itself does not change, so no content is re-encrypted.
 */
export async function rewrapDekForConversion(
  client: AccountSupabaseClient,
  userId: string,
  dek: CryptoKey,
  passphrase: string
): Promise<void> {
  await storePassphraseWrappedKey(client, userId, dek, passphrase)
}

/**
 * Requests the email change from the derived anonymous address to the real
 * one. Supabase confirms it via email when confirmations are enabled.
 */
export async function requestAccountEmailChange(
  client: AccountSupabaseClient,
  email: string,
  redirectTo?: string
): Promise<{ error: string | null }> {
  const { error } = await client.auth.updateUser(
    { email },
    redirectTo ? { emailRedirectTo: redirectTo } : undefined
  )
  return { error: errorMessage(error) }
}

/**
 * Whether the email change has been applied and confirmed.
 */
export async function isEmailChangeConfirmed(
  client: AccountSupabaseClient,
  email: string
): Promise<boolean> {
  const { data } = await client.auth.getUser()
  const user = data?.user
  return (
    user?.email === email && user?.email_confirmed_at !== null && user?.email_confirmed_at !== undefined
  )
}

/**
 * Finalises seed conversion: the DEK is already re-wrapped, so this only
 * clears the derived password and makes the account OTP-only.
 */
export async function finalizeAccountConversion(
  client: AccountSupabaseClient
): Promise<{ error: string | null }> {
  const { error } = await client.rpc('convert_to_otp_only')
  return { error: errorMessage(error) }
}

export async function signOutAccount(
  client: AccountSupabaseClient
): Promise<void> {
  const { error } = await client.auth.signOut()
  if (error) throw new Error(error.message)
}

async function storePassphraseWrappedKey(
  client: AccountSupabaseClient,
  userId: string,
  dek: CryptoKey,
  passphrase: string
): Promise<void> {
  const wrapped = await wrapDekWithPassphrase(dek, passphrase)

  const { error } = await client.from('user_keys').upsert(
    {
      user_id: userId,
      wrapped_key: wrapped,
      algorithm: OTP_KEY_ALGORITHM,
      key_version: OTP_KEY_VERSION,
      updated_at: new Date().toISOString()
    },
    { onConflict: 'user_id' }
  )
  if (error) throw new Error(error.message)

  await cacheDerivedPassphraseKey(wrapped, passphrase)
}

async function cacheDerivedPassphraseKey(
  wrapped: string,
  passphrase: string
): Promise<void> {
  const saltB64 = wrapped.split('.')[0]
  if (!saltB64) throw new Error('Invalid passphrase-wrapped key format')

  const wrappingKey = await derivePassphraseKey(
    passphrase,
    base64ToArrayBuffer(saltB64)
  )
  await cachePassphraseKey(wrappingKey)
}
